import type { FinAdvanceRetentionRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { IconPlus } from '@shared/ui/icons';
import { formatDateRu, formatMoney } from '../financierFormat';
import {
  Section,
  SectionHeader,
  SectionTitle,
  Subtitle,
  TableWrap,
  RightTh,
  RightTd,
  TableNoticeTd,
  CounterpartyTitle,
  InnSubText,
  BoldText,
  DimSubText,
  PaidGreenTd,
  RemainingRedTd,
  IssuedDateText,
} from './styled';
import { ADVANCE_STATUS_LABELS } from './types';

interface AdvancesSectionProps {
  advanceRows: FinAdvanceRetentionRow[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  onOpenAdvModal: () => void;
  canWrite: boolean;
}

export function AdvancesSection({
  advanceRows,
  isLoading,
  isError,
  refetch,
  onOpenAdvModal,
  canWrite,
}: AdvancesSectionProps) {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>Реестр авансов (выданных поставщикам и полученных от Заказчиков)</SectionTitle>
          <Subtitle>
            Контроль закрытия авансов формами КС-2 и товарными накладными МТО с детекцией просрочки
          </Subtitle>
        </div>
        {canWrite && (
          <Button $variant="secondary" $size="sm" onClick={onOpenAdvModal}>
            <IconPlus size={14} />
            Добавить аванс / удержание
          </Button>
        )}
      </SectionHeader>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Направление</th>
              <th>Контрагент / ИНН</th>
              <th>Проект / Договор</th>
              <RightTh>Выдано аванса, ₽</RightTh>
              <RightTh>Отработано (КС-2/УПД)</RightTh>
              <RightTh>Не закрытый остаток, ₽</RightTh>
              <th>Срок закрытия</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <TableNoticeTd colSpan={8}>
                  Загрузка авансов…
                </TableNoticeTd>
              </tr>
            ) : isError ? (
              <tr>
                <TableNoticeTd colSpan={8}>
                  Не удалось загрузить реестр авансов и удержаний.{' '}
                  <Button $variant="secondary" $size="sm" onClick={refetch}>
                    Повторить
                  </Button>
                </TableNoticeTd>
              </tr>
            ) : advanceRows.length === 0 ? (
              <tr>
                <TableNoticeTd colSpan={8}>
                  Авансы не зарегистрированы
                </TableNoticeTd>
              </tr>
            ) : (
              advanceRows.map((a: FinAdvanceRetentionRow) => {
                const statusConf = ADVANCE_STATUS_LABELS[a.status] ?? {
                  label: a.status,
                  tone: 'neutral',
                };
                return (
                  <tr key={a.id}>
                    <td>
                      <Badge
                        $tone={a.direction === 'payable_advance' ? 'warning' : 'success'}
                      >
                        {a.direction === 'payable_advance' ? 'Выданный нами' : 'Полученный'}
                      </Badge>
                    </td>
                    <td>
                      <CounterpartyTitle>{a.counterparty_name}</CounterpartyTitle>
                      {a.counterparty_inn && (
                        <InnSubText>
                          ИНН: {a.counterparty_inn}
                        </InnSubText>
                      )}
                    </td>
                    <td>
                      <BoldText>{a.project_name}</BoldText>
                      <DimSubText>
                        Договор № {a.contract_number}
                      </DimSubText>
                    </td>
                    <RightTd>
                      <div>{formatMoney(a.initial_amount)}</div>
                    </RightTd>
                    <PaidGreenTd>
                      {formatMoney(a.settled_amount)}
                    </PaidGreenTd>
                    <RemainingRedTd>
                      {formatMoney(a.remaining_amount)}
                    </RemainingRedTd>
                    <td>
                      <div>{formatDateRu(a.expected_close_date)}</div>
                      <IssuedDateText>
                        Выдан: {formatDateRu(a.issue_date)}
                      </IssuedDateText>
                    </td>
                    <td>
                      <Badge $tone={statusConf.tone}>{statusConf.label}</Badge>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
